import {
  DownloadOutlined,
  ExportOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import JsonView from '@uiw/react-json-view';
import { monokaiTheme } from '@uiw/react-json-view/monokai';
import { Button, Dropdown, Empty, type MenuProps, Space, Spin } from 'antd';
import { t } from 'i18next';
import React, { useContext, useMemo, useCallback } from 'react';

import { Processing } from '@client/components/loader/Processing';
import { NotificationContext } from '@client/context/NotificationContext';
import { useJson } from '@client/hooks/useJson';
import { exportAsCSV, exportAsJSON } from '@client/utils/file.util';

import styles from './buttons.module.less';

import type { ButtonSize, ButtonType } from 'antd/es/button';

type TExportButtonProps<T> = {
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  preview?: boolean;
  subject: string;
  isMenuItem?: boolean;
  refTarget?: React.RefObject<HTMLElement | null>;
  json: T;
  button?: {
    type: ButtonType;
    size: ButtonSize;
  };
};

export const ExportButton = <T = unknown,>(props: TExportButtonProps<T>) => {
  const { modalApi } = useContext(NotificationContext);
  const { workerLoading, workerData } = useJson<T>({ json: props.json });

  const {
    className,
    loading,
    disabled = false,
    preview = false,
    isMenuItem = true,
    subject,
    button = {
      type: 'text',
      size: 'small',
    },
  } = props;

  // Wrap showPreviewModal in useCallback to stabilize the function reference
  const showPreviewModal = useCallback(() => {
    modalApi?.info({
      title: t('actions.export'),
      icon: <ExportOutlined />,
      width: 550,
      centered: true,
      closable: true,
      wrapClassName: 'exportModal',
      content: (
        <div
          style={{
            minHeight: '300px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {workerLoading ? (
            <Processing
              fullscreen={false}
              description={'Processing JSON data...'}
            />
          ) : workerData?.length ? (
            <JsonView value={workerData} style={monokaiTheme} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </div>
      ),
      onOk: () => {
        exportAsJSON(workerData, subject);
      },
      okButtonProps: {
        size: 'small',
        icon: <ExportOutlined />,
      },
      okText: t('actions.export'),
    });
  }, [modalApi, workerLoading, workerData, subject]); // Depend only on used values

  const previewItems = useMemo(
    () =>
      [
        {
          label: (
            <div onClick={showPreviewModal}>{t('actions.preview')}</div>
          ) as React.JSX.Element,
          key: 'preview',
          icon: <EyeOutlined />,
        },
        { type: 'divider' as const }, // Explicitly type as const
      ] as MenuProps['items'],
    [showPreviewModal],
  ); // Now showPreviewModal is stable due to useCallback

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [
      {
        label: (
          <Button
            loading={loading}
            size={button?.size}
            type={button?.type}
            className={styles.jsonBtn}
            onClick={() => exportAsJSON(workerData, subject)}
          >
            {t('actions.exportAs', { type: '' })}
          </Button>
        ),
        key: 'exportJson',
        icon: <ExportOutlined />,
        extra: (
          <Button
            loading={loading}
            size="small"
            color="primary"
            variant="filled"
          >
            JSON
          </Button>
        ),
      },
      {
        label: (
          <Button
            loading={loading}
            size={button?.size}
            type={button?.type}
            className={styles.jsonBtn}
            onClick={() => exportAsCSV<any>(workerData, subject)}
          >
            {t('actions.exportAs', { type: '' })}
          </Button>
        ),
        key: 'exportCsv',
        icon: <ExportOutlined />,
        extra: (
          <Button
            loading={loading}
            size="small"
            color="primary"
            variant="filled"
          >
            CSV
          </Button>
        ),
      },
    ];

    if (preview) {
      items.unshift(...(previewItems ?? []));
    }

    return items;
  }, [loading, button, workerData, subject, preview, previewItems]);

  if (isMenuItem) {
    return {
      className,
      // Remove unsupported `loading` prop to avoid React DOM warning
      disabled: Boolean(disabled),
      key: 'export',
      label: loading ? (
        <span>
          <Spin size="small" style={{ marginRight: 8 }} />
          {t('actions.export')}
        </span>
      ) : (
        t('actions.export')
      ),
      icon: loading ? <Spin size="small" /> : <DownloadOutlined />,
      children: menuItems,
    };
  }

  return (
    <Space.Compact>
      <Dropdown disabled={disabled} menu={{ items: menuItems }}>
        <Button
          loading={loading}
          disabled={disabled}
          icon={loading ? <Spin size="small" /> : <DownloadOutlined />}
        >
          {loading ? (
            <span>
              <Spin size="small" style={{ marginRight: 8 }} />
              {t('actions.export')}
            </span>
          ) : (
            t('actions.export')
          )}
        </Button>
      </Dropdown>
    </Space.Compact>
  );
};
